import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '@/components/buttons';
import InputField from '@/components/inputs/inputField';
import { editReference, getReference } from '@/communication/reference';
import { ServerError } from '@/types/communication/serverResponse';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { BackButton } from '@/components/buttons/backButton';

interface Props {
    createMode?: boolean;
}

interface FormValues {
    content: string;
    name: string;
    location: string;
    image: string | File;
}

const ReferenceDetails: FunctionComponent<Props> = (props) => {
    const { createMode } = props;
    const { referenceId: referenceIdParam } = useParams();
    const referenceId = Number(referenceIdParam);
    const nav = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formValues, setFormValues] = useState<FormValues>({
        content: '',
        name: '',
        location: '',
        image: '',
    });
    const [validationErrors, setValidationErrors] = useState<any>();
    const { data, status } = useFetch({
        queryFn: () => getReference(referenceId),
        disabled: Number.isNaN(referenceId),
    });

    const handleChange = (fieldName: keyof FormValues, value): void => {
        setFormValues((prevValues) => ({ ...prevValues, [fieldName]: value }));
    };

    const handleSubmit = async (): Promise<void> => {
        setIsSubmitting(true);
        setValidationErrors(null);
        try {
            await editReference({ id: referenceId, ...formValues });
            nav('/references');
        } catch (error) {
            const serverError = error as ServerError;
            if (serverError?.type === 'validation') {
                setValidationErrors(serverError.payload);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (status === 'fetched' && data) {
            setFormValues(data);
        }
    }, [data, status]);

    if (status === 'loading') {
        return <>Loading</>;
    }
    return (
        <StyledContent>
            <h1>{createMode ? 'Create' : 'Edit'} Reference</h1>
            <InputField
                value={formValues.content}
                label="Content"
                errorMessage={validationErrors?.content}
                onChange={(e) => handleChange('content', e.target.value)}
            />
            <InputField
                value={formValues.name}
                label="Name"
                errorMessage={validationErrors?.name}
                onChange={(e) => handleChange('name', e.target.value)}
            />
            <InputField
                value={formValues.location}
                label="Location"
                errorMessage={validationErrors?.location}
                onChange={(e) => handleChange('location', e.target.value)}
            />
            <InputField
                label="Logo"
                type="file"
                errorMessage={validationErrors?.image}
                onChange={(e: React.FormEvent<HTMLInputElement>) => handleChange('image', e.currentTarget.files?.[0])}
            />
            <div className="form-actions">
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                    {createMode ? 'Create' : 'Save'}
                </Button>
                <BackButton />
            </div>
        </StyledContent>
    );
};

const StyledContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 1140px;
    margin: auto;
    padding-left: 1rem;
    padding-right: 1rem;
    .form-actions {
        display: flex;
        gap: 1rem;
        margin-bottom: 5rem;
    }
`;

export default ReferenceDetails;
